import type { RoadTripWaypoint } from '../types/roadTrip';
import { format } from 'date-fns';

interface RoadTripWaypointCardProps {
  waypoint: RoadTripWaypoint;
  index: number;
  totalWaypoints: number;
  onUpdate: (updates: Partial<RoadTripWaypoint>) => void;
  onRemove: () => void;
}

export default function RoadTripWaypointCard({
  waypoint,
  index,
  totalWaypoints,
  onUpdate,
  onRemove
}: RoadTripWaypointCardProps) {
  const getTypeIcon = (type: RoadTripWaypoint['type']) => {
    switch (type) {
      case 'start': return '🏁';
      case 'end': return '🏆';
      case 'overnight': return '🌙';
      case 'attraction': return '📍';
      default: return '📌';
    }
  };

  const getTypeLabel = (type: RoadTripWaypoint['type']) => {
    switch (type) {
      case 'start': return 'Start';
      case 'end': return 'End';
      case 'overnight': return 'Overnight';
      case 'attraction': return 'Attraction';
      default: return 'Stop';
    }
  };

  return (
    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      {/* Order Number */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
        index === 0 ? 'bg-green-500 text-white' :
        index === totalWaypoints - 1 ? 'bg-red-500 text-white' :
        'bg-primary-500 text-white'
      }`}>
        {index + 1}
      </div>

      {/* Waypoint Info */}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{getTypeIcon(waypoint.type)}</span>
          <h3 className="font-medium text-gray-900">{waypoint.name}</h3>
          <span className="text-xs px-2 py-0.5 bg-gray-200 rounded-full text-gray-600">
            {getTypeLabel(waypoint.type)}
          </span>
        </div>
        <p className="text-sm text-gray-500 mb-2">{waypoint.location.name}</p>

        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>⏱️ {waypoint.duration}h visit</span>
          {waypoint.isRequired && (
            <span className="text-amber-600">✓ Required</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onUpdate({ isRequired: !waypoint.isRequired })}
          className={`p-2 rounded-lg transition-colors ${
            waypoint.isRequired
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
          }`}
          title={waypoint.isRequired ? 'Mark as optional' : 'Mark as required'}
        >
          {waypoint.isRequired ? '★' : '☆'}
        </button>
        <button
          onClick={onRemove}
          className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
          title="Remove stop"
        >
          🗑️
        </button>
      </div>
    </div>
  );
}
